// The exactly-once key every offline-capable mutation carries.
//
// ⚠ WHY THIS EXISTS AND IS NOT JUST `crypto.randomUUID()`.
// `crypto.randomUUID` is defined ONLY IN A SECURE CONTEXT. Both consoles are
// served over plain HTTP on an origin the browser does not trust:
//
//   http://10.255.254.240:3000   the site console, on the farm LAN
//   http://0.0.0.0:3001          the cloud console on a laptop
//
// Neither is `localhost` or `127.0.0.1/8`, so `window.isSecureContext` is false
// and `crypto.randomUUID` is `undefined` — calling it throws a TypeError. Every
// mutation that minted its own `client_ref` inline therefore died on the real
// hardware: the create never left the browser, and because the call sites were
// `void`-ed with no catch, the button simply did nothing.
//
// `crypto.getRandomValues` is NOT gated on secure context (only `randomUUID` and
// `crypto.subtle` are), and it is present on both origins — measured, not
// assumed. So the fallback is a real RFC-4122 v4 with full CSPRNG entropy, not a
// `Math.random` impostor. There is no weaker path: if `getRandomValues` is
// missing too, we throw rather than mint a guessable idempotency key.

/** A fresh RFC-4122 v4 UUID, in or out of a secure context.
 *
 * Use for any `client_ref` / idempotency key. NEVER call `crypto.randomUUID()`
 * directly — it is unavailable on both of this product's real origins. */
export function newClientRef(): string {
  // The native path when the browser gives us one; identical output shape.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') {
    // Refusing beats improvising: a guessable client_ref silently defeats the
    // exactly-once guarantee it exists to provide.
    throw new Error('No CSPRNG available — cannot mint a client_ref');
  }
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6]! & 0x0f) | 0x40; // version 4
  b[8] = (b[8]! & 0x3f) | 0x80; // variant 10xx
  const hex: string[] = [];
  for (let i = 0; i < 16; i++) hex.push(b[i]!.toString(16).padStart(2, '0'));
  return (
    `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-` +
    `${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`
  );
}
